import type { UseCurrentUserFunction } from '@shared/stores/current-user'
import { $t } from 'src/plugins/i18n'
import type { ProxyRecord } from 'src/services/deserializers/deserializers.types'
import { relationsEnumRule } from 'src/services/mappings/files.utils'
import {
  enumLabelIsKey,
  standardDateControls,
} from 'src/services/mappings/mapping.utils'
import {
  CLIENT_TYPES,
  PERSON_CREATION_TYPES,
} from 'src/services/mappings/person/person.const'
import {
  alternatedResidenceIsHidden,
  legalCapacityDynamicEnumRules,
} from 'src/services/mappings/person/person.mapping.utils'
import type {
  DynamicEnumsRuleInterface,
  FieldInterface,
  RelationInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import {
  IS_NESTED,
  OBJECT_TYPES,
} from 'src/tpbp-configuration/models/models.const'
import { withTranslate } from 'src/tpbp-configuration/utils/i18n.utils'
import {
  FIELD_DISPLAY_OPTIONS,
  FIELD_TYPES,
} from 'src/tpbp-core/models/fields/field-set.const'
import {
  FIELD_RULES,
  MAX_NATIONALITIES_PER_PERSON,
} from 'src/tpbp-core/models/fields/fieldRules.const'
import { phoneNumberValidator } from 'src/tpbp-core/models/fields/fields.validator'
import { TEXT_INPUT_FORMATS } from 'src/tpbp-core/models/fields/text-input.const'
import type { PersonModel } from 'src/tpbp-json-api-handler/models/models'
import * as PersonValidations from './person.validation'

const contextualDynamicRules = [
  {
    type: FIELD_RULES.isMandatory,
    expression: (
      record: ProxyRecord,
      { personIsMandatory, fieldStatusesByOriginKey },
      { originKey },
    ) => personIsMandatory(record, originKey, fieldStatusesByOriginKey),
  },
  {
    type: FIELD_RULES.isOptional,
    expression: (
      record: ProxyRecord,
      { personIsOptional, fieldStatusesByOriginKey },
      { originKey },
    ) => personIsOptional(record, originKey, fieldStatusesByOriginKey),
  },
  {
    type: FIELD_RULES.isReadOnly,
    expression: (
      record: ProxyRecord,
      { personIsReadOnly, fieldStatusesByOriginKey },
      { originKey },
    ) => personIsReadOnly(record, originKey, fieldStatusesByOriginKey),
  },
]

const contextualDynamicEnumRules: DynamicEnumsRuleInterface[] = [
  {
    expression: () => true,
    getEnumKeys: (record, { getPersonEnums }, field) =>
      getPersonEnums(record, field),
  },
]

const alternatedResidenceDynamicRules = [
  ...contextualDynamicRules,
  {
    type: FIELD_RULES.isHidden,
    expression: (record) => alternatedResidenceIsHidden(record),
  },
]

const creationTypeEnumRules = [
  {
    expression: () => true,
    getEnumKeys: (
      _record,
      { useCurrentUser }: { useCurrentUser: UseCurrentUserFunction },
    ) => Object.values(useCurrentUser().userAvailableCreationTypes.value),
  },
]

const personFields: FieldInterface[] = [
  // /!\ ADDING THIS BREAK THE APP
  // {
  //   key: 'id',
  //   originKey: 'id',
  // },
  {
    key: 'fullName',
    originKey: 'full_name',
    type: FIELD_TYPES.string,
    isPseudoAttribute: true,
    rule: FIELD_RULES.isReadOnly,
    expression: (record: PersonModel) =>
      `${record.firstName} ${record.lastName}`,
  },
  {
    key: 'isOwner',
    originKey: 'isOwner',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
    isPseudoAttribute: true,
  },
  {
    key: 'creationType',
    originKey: 'creation_type',
    type: FIELD_TYPES.string,
    enumKeys: Object.values(PERSON_CREATION_TYPES),
    default: 'child',
    dynamicRules: [
      {
        type: FIELD_RULES.isReadOnly,
        expression: (record: PersonModel) => !record.$isNew,
      },
      {
        type: FIELD_RULES.isMandatory,
        expression: (record: PersonModel) => record.$isNew,
      },
      {
        type: FIELD_RULES.isHidden,
        expression: (record: PersonModel) => !record.$isNew,
      },
    ],
    enumRules: creationTypeEnumRules,
  },
  {
    key: 'civility',
    originKey: 'civility',
    type: FIELD_TYPES.string,
    enumKeys: [], // this is needed for severals unit tests 💩
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'lastName',
    originKey: 'last_name',
    type: FIELD_TYPES.string,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'firstName',
    originKey: 'first_name',
    type: FIELD_TYPES.string,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'birthName',
    originKey: 'birth_name',
    type: FIELD_TYPES.string,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'dateOfBirth',
    originKey: 'date_of_birth',
    type: FIELD_TYPES.date,
    displayOption: FIELD_DISPLAY_OPTIONS.withAge,
    default: null,
    dynamicRules: contextualDynamicRules,
    dependentFieldKeys: ['age'],
    controls: {
      min: PersonValidations.validatePersonMinDateOfBirthControl,
      max: PersonValidations.validatePersonMaxDateOfBirthControl,
    },
  },
  {
    key: 'age',
    originKey: 'age',
    type: FIELD_TYPES.integer,
    default: null,
    rule: FIELD_RULES.isReadOnly,
    expression: (record, helpers) => helpers.getPersonAge(record),
  },
  {
    key: 'email',
    originKey: 'email',
    type: FIELD_TYPES.string,
    format: TEXT_INPUT_FORMATS.email,
    iconPath: 'monochrome/email.svg',
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'homePhoneNumber',
    originKey: 'home_phone_number',
    type: FIELD_TYPES.string,
    iconPath: 'monochrome/phone.svg',
    displayBreak: true,
    default: null,
    dynamicRules: contextualDynamicRules,
    format: TEXT_INPUT_FORMATS.phone,
    getRules: () => [withTranslate(phoneNumberValidator)],
  },
  {
    key: 'mobilePhoneNumber',
    originKey: 'mobile_phone_number',
    type: FIELD_TYPES.string,
    iconPath: 'monochrome/phone.svg',
    default: null,
    dynamicRules: contextualDynamicRules,
    format: TEXT_INPUT_FORMATS.phone,
    getRules: () => [withTranslate(phoneNumberValidator)],
  },
  {
    key: 'maritalStatus',
    originKey: 'marital_status',
    type: FIELD_TYPES.string,
    enumKeys: [
      'single',
      'divorced',
      'married',
      'separated',
      'free_union',
      'widow',
      'pacs',
    ],
    default: null,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumType: 'MaritalStatuses',
    enumTypePath:
      'src/circuits/transmission-circuit/shared/transmission-circuit.const.ts',
  },
  {
    key: 'maritalClause',
    originKey: 'marital_clause',
    helpMessageId: '2',
    type: FIELD_TYPES.string,
    enumKeys: [
      'none',
      'clause_of_preciput',
      'inegal_attribution',
      'integral_attribution',
      'good_aquisition_or_attribution_faculty',
    ],
    default: null,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'matrimonialRegime',
    originKey: 'matrimonial_regime',
    helpMessageId: '34',
    type: FIELD_TYPES.string,
    enumKeys: [
      'legal_regime_start_date_after_01_02_1966',
      'reduced_community_of_acquests',
      'universal',
      'full_universal',
      'legal_regime_start_date_before_01_02_1966',
      'community_of_furniture_and_acquests',
      'separation_of_property',
      'participation_to_acquests',
      'separation_with_acquests_company',
      'foreign',
      'optional_participation_of_acquests',
    ],
    default: null,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'matrimonialRegimeStartDate',
    originKey: 'matrimonial_regime_start_date',
    type: FIELD_TYPES.date,
    default: null,
    dynamicRules: contextualDynamicRules,
    controls: standardDateControls, // TODO find rule in json schema
  },
  {
    key: 'ddvReciprocity',
    originKey: 'ddv_reciprocity',
    helpMessageId: '11',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'consentedDdvReciprocity',
    originKey: 'consented_ddv_reciprocity',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'ddvReciprocityType',
    originKey: 'ddv_reciprocity_type',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    enumKeys: [
      'totality_in_usufruct',
      'three_fourths_usufruct_and_one_fourth_full_ownership',
      'available_portion_in_full_ownership',
      'hundred_percent_in_full_ownership',
      'none',
    ],
  },
  {
    key: 'consentedDdvReciprocityType',
    originKey: 'consented_ddv_reciprocity_type',
    type: FIELD_TYPES.string,
    enumKeys: [],
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'attributionClausePresent',
    originKey: 'attribution_clause_present',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'pacsConvention',
    originKey: 'pacs_convention',
    type: FIELD_TYPES.string,
    enumKeys: [],
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'legalCapacity',
    originKey: 'legal_capacity',
    type: FIELD_TYPES.string,
    enumKeys: [
      'adult',
      'adult_under_guardianship',
      'adult_under_curatorship',
      'adult_under_legal_protection',
      'minor',
      'minor_emancipated',
      'minor_under_judicial_control',
      'minor_under_guardianship',
    ],
    default: 'minor',
    dynamicRules: contextualDynamicRules,
    enumRules: legalCapacityDynamicEnumRules,
  },
  {
    key: 'ifiTaxRate',
    originKey: 'ifi_tax_rate',
    type: FIELD_TYPES.integer,
    default: null,
    rule: FIELD_RULES.isHidden,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'fiscalResidence',
    originKey: 'fiscal_residence',
    type: FIELD_TYPES.string,
    enumKeys: [],
    default: null,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'nationalities',
    originKey: 'nationalities',
    type: FIELD_TYPES.string,
    isMultiple: true,
    maxValues: 4,
    enumKeys: [],
    default: null,
    getRules: () => [
      (v) =>
        v.length <= MAX_NATIONALITIES_PER_PERSON ||
        $t('models.people.errors.max-nationalities', {
          max: MAX_NATIONALITIES_PER_PERSON,
        }),
    ],
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'numberOfMinorChildren',
    originKey: 'number_of_minor_children',
    type: FIELD_TYPES.integer,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'totalNumberOfChildren',
    originKey: 'total_number_of_children',
    type: FIELD_TYPES.integer,
    default: 0,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'numberOfOwnChildren',
    originKey: 'number_of_own_children',
    type: FIELD_TYPES.integer,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'numberOfChildrenInCommon',
    originKey: 'number_of_children_in_common',
    type: FIELD_TYPES.integer,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'financiallyDependent',
    originKey: 'financially_dependent',
    helpMessageId: '29',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'ageOfFinancialIndependence',
    originKey: 'age_of_financial_independence',
    type: FIELD_TYPES.integer,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'careerPeriod',
    originKey: 'career_period',
    type: FIELD_TYPES.integer,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'careerPoints',
    originKey: 'career_points',
    type: FIELD_TYPES.integer,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'trimesters',
    originKey: 'trimesters',
    type: FIELD_TYPES.integer,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isPpe',
    originKey: 'is_ppe',
    type: FIELD_TYPES.boolean,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isNonResident',
    originKey: 'is_non_resident',
    type: FIELD_TYPES.boolean,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isCrossBorder',
    originKey: 'is_cross_border',
    type: FIELD_TYPES.boolean,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isDualCitizen',
    originKey: 'is_dual_citizen',
    type: FIELD_TYPES.boolean,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isUsPerson',
    originKey: 'is_us_person',
    type: FIELD_TYPES.boolean,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isRetired',
    originKey: 'is_retired',
    type: FIELD_TYPES.boolean,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'handicapped',
    originKey: 'handicapped',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'handicapPercentage',
    originKey: 'handicap_percentage',
    type: FIELD_TYPES.integer,
    default: null,
    controls: {
      max: () => 99, // TODO check if this control is in configuration
    },
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'adopted',
    originKey: 'adopted',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'isEligibleForAggregation',
    originKey: 'is_eligible_for_aggregation',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isHidden,
  },
  {
    key: 'adoptionType',
    originKey: 'adoption_type',
    type: FIELD_TYPES.string,
    enumKeys: ['full', 'simple'],
    default: null,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'alternatedResidence',
    originKey: 'alternated_residence',
    type: FIELD_TYPES.boolean,
    default: false,
    dynamicRules: alternatedResidenceDynamicRules,
  },
  {
    key: 'deceased',
    originKey: 'deceased',
    type: FIELD_TYPES.boolean,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'clientType',
    originKey: 'client_type',
    enumKeys: Object.values(CLIENT_TYPES),
    type: FIELD_TYPES.string,
    default: null,
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
  },
  {
    key: 'careerStartDate',
    originKey: 'career_start_date',
    type: FIELD_TYPES.date,
    default: null,
    controls: standardDateControls,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'careerStartSalaryAmount',
    originKey: 'career_start_salary_amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'numberOfTrimesterRedeemed',
    originKey: 'number_of_trimester_redeemed',
    type: FIELD_TYPES.integer,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'globalConsentForAggregation',
    originKey: 'global_consent_for_aggregation',
    type: FIELD_TYPES.boolean,
    default: false,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'tmi',
    originKey: 'tmi',
    type: FIELD_TYPES.integer,
    default: null,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'financiallyDependentFrom',
    originKey: 'financially_dependent_from_id',
    targetKey: 'id',
    originRelationship: '_financiallyDependentFrom',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
    enumKeys: [],
    enumRules: relationsEnumRule,
  },
  {
    key: 'taxableIncome',
    originKey: 'taxable_income',
    helpMessageId: '40',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxIncome',
    originKey: 'tax_income',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxRealEstateFortune',
    originKey: 'tax_real_estate_fortune',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxExceptionalHighIncome',
    originKey: 'tax_exceptional_high_income',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxSocialContributions',
    originKey: 'tax_social_contributions',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxAllowanceUsed',
    originKey: 'tax_allowance_used',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxAllowanceAvailable',
    originKey: 'tax_allowance_available',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxAllowanceUsedRetirement',
    originKey: 'tax_allowance_used_retirement',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxAllowanceAvailableRetirement',
    originKey: 'tax_allowance_available_retirement',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    default: null,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'taxRateOnMarginalIncome',
    originKey: 'tax_rate_on_marginal_income',
    type: FIELD_TYPES.string,
    default: null,
    enumKeys: [],
    dynamicRules: contextualDynamicRules,
    enumRules: contextualDynamicEnumRules,
    dynamicEnumLabel: enumLabelIsKey,
  },
  {
    key: 'taxDataUpdatedAt',
    originKey: 'tax_data_updated_at',
    type: FIELD_TYPES.string,
    dynamicRules: contextualDynamicRules,
  },
  {
    key: 'lclExternalId',
    originKey: 'lcl_external_id',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'attributionClauseFullOwnershipPart',
    originKey: 'attribution_clause_full_ownership_part',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'attributionClauseBareOnwershipPart',
    originKey: 'attribution_clause_bare_onwership_part',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'attributionClauseBareOwnershipTerm',
    originKey: 'attribution_clause_bare_ownership_term',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'attributionClauseUsufructPart',
    originKey: 'attribution_clause_usufruct_part',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'attributionClauseUsufructTerm',
    originKey: 'attribution_clause_usufruct_term',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'endDateOfAttachment',
    originKey: 'end_date_of_attachment',
    type: FIELD_TYPES.date,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'numberOfAttached_People',
    originKey: 'number_of_attached_people',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'singleParent',
    originKey: 'single_parent',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'singleParentForFiveYears',
    originKey: 'single_parent_for_five_years',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'heritageSubjectToIfi',
    originKey: 'heritage_subject_to_ifi',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'taxAwardedPension',
    originKey: 'tax_awarded_pension',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'numberOfChildrenRaised',
    originKey: 'number_of_children_raised',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isSingleParent',
    originKey: 'is_single_parent',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'lengthOfPreviousMariages',
    originKey: 'length_of_previous_mariages',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'dateOfLastMeeting',
    originKey: 'date_of_last_meeting',
    type: FIELD_TYPES.date,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'heldParts',
    originKey: 'held_parts',
    type: FIELD_TYPES.date,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'estimatedRetirementAge',
    originKey: 'estimated_retirement_age',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'flatRateLevy',
    originKey: 'flat_rate_levy',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
]

const personRelations: RelationInterface[] = [
  // TODO check if all relationships are present
  {
    key: '_creator',
    originKey: 'creator',
    objectType: OBJECT_TYPES.person,
    isMultiple: false,
  },
  {
    key: '_financiallyDependentFrom',
    originKey: 'financially_dependent_from',
    objectType: OBJECT_TYPES.person,
    isMultiple: false,
    // TODO use relationship in serialize methods instead of nested
    isNested: IS_NESTED.yes,
    nestedKey: 'financially_dependent_from_id',
  },
  {
    key: '_extended_relations',
    originKey: 'extended_relations',
    objectType: OBJECT_TYPES.relation,
    isMultiple: true,
    isNested: IS_NESTED.ifNew,
  },
  {
    key: '_relations',
    originKey: 'relations',
    objectType: OBJECT_TYPES.relation,
    isMultiple: true,
    isNested: IS_NESTED.ifNew,
  },
  {
    key: '_households',
    originKey: 'households',
    objectType: OBJECT_TYPES.household,
    isMultiple: true,
  },
  {
    key: '_defaultHousehold',
    originKey: 'default_household',
    objectType: OBJECT_TYPES.household,
    isMultiple: false,
  },
  {
    key: '_charge',
    originKey: 'charge',
    objectType: OBJECT_TYPES.genericModel,
    isMultiple: false,
  },
  {
    key: '_income',
    originKey: 'income',
    objectType: OBJECT_TYPES.genericModel,
    isMultiple: false,
  },
  {
    key: '_portfolios',
    originKey: 'portfolios',
    objectType: OBJECT_TYPES.portfolio,
    isMultiple: true,
  },
  {
    key: '_donations',
    originKey: 'donations',
    objectType: OBJECT_TYPES.donation,
    isMultiple: true,
  },
  {
    key: '_budgetItems',
    originKey: 'budget_items',
    objectType: OBJECT_TYPES.budgetItem,
    isMultiple: true,
  },
  {
    key: '_professions',
    originKey: 'professions',
    objectType: OBJECT_TYPES.profession,
    isMultiple: true,
    isNested: IS_NESTED.ifNew,
  },
  {
    key: '_receivedDonations',
    originKey: 'received_donations',
    objectType: OBJECT_TYPES.donation,
    isMultiple: true,
  },
  {
    key: '_relationsMap',
    originKey: 'relations_map',
    objectType: OBJECT_TYPES.genericModel,
    isMultiple: false,
  },
  {
    key: '_nniData',
    originKey: 'nni_data',
    objectType: OBJECT_TYPES.nniData,
    isMultiple: false,
  },
]

export { personFields, personRelations }
